import React from 'react';

// Main Card Component
export type CardVariant = 'default' | 'elevated' | 'outlined' | 'glass' | 'glass-dark';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: CardVariant;
}

const Card: React.FC<CardProps> & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
} = ({ children, className = '', variant = 'default' }) => {
  const variantStyles: Record<CardVariant, string> = {
    default: 'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 ease-in-out',
    elevated: 'bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-in-out',
    outlined: 'bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all duration-300 ease-in-out',
    glass: 'bg-white/70 dark:bg-gray-900/70 backdrop-blur-lg rounded-2xl shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] border border-white/30 dark:border-gray-700/30 hover:bg-white/85 dark:hover:bg-gray-900/85 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.25)] dark:hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.6)] hover:-translate-y-1 transition-all duration-300 ease-in-out',
    'glass-dark': 'bg-gray-900/30 backdrop-blur-lg rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] border border-white/10 hover:bg-gray-900/40 hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] hover:-translate-y-1 transition-all duration-300 ease-in-out text-white',
  };

  const cardClasses = `${variantStyles[variant]} ${className}`;

  return <div className={cardClasses}>{children}</div>;
};

// Card Header Component
interface CardHeaderProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  actions,
  className = '',
  children,
}) => {
  return (
    <div className={`p-6 pb-4 ${className}`}>
      {children || (
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {title && (
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
            )}
          </div>
          {actions && <div className="ml-4">{actions}</div>}
        </div>
      )}
    </div>
  );
};

// Card Body Component
interface CardBodyProps {
  children: React.ReactNode;
  className?: string;
}

const CardBody: React.FC<CardBodyProps> = ({ children, className = '' }) => {
  return <div className={`px-6 py-4 ${className}`}>{children}</div>;
};

// Card Footer Component
interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  justify?: 'start' | 'end' | 'center' | 'between' | 'around';
}

const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
  justify = 'end',
}) => {
  const justifyStyles = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
    between: 'justify-between',
    around: 'justify-around',
  };

  return (
    <div
      className={`px-6 py-4 pt-0 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center ${justifyStyles[justify]} ${className}`}
    >
      {children}
    </div>
  );
};

// Attach sub-components to Card
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
